// import React, { useState } from 'react';
// import PropTypes from 'prop-types';
// import '../DetailedAnalysis/DetailedAnalysis.css';

// const MetricCard = ({ metric, categoryName }) => {
//   const [expanded, setExpanded] = useState(false);

//   const getThreshold = () => {
//     const match = metric.referenceRange?.match(/(\d+\.?\d*)\s*%?\s*\+/);
//     if (match) return parseFloat(match[1]);
//     const lessThanMatch = metric.referenceRange?.match(/<\s*(\d+\.?\d*)/);
//     if (lessThanMatch) return parseFloat(lessThanMatch[1]);
//     return null;
//   };

//   const threshold = getThreshold();
//   const whoLabel = metric.referenceRange?.replace('Normal:', 'WHO');
//   const value = parseFloat(metric.value);
//   const position = Math.min(Math.max(value, 0), 100);

//   const color = metric.status === 'success' ? '#22c55e' : metric.status === 'warning' ? '#f59e0b' : '#ef4444';

//   return (
//     <div className="metric-card">
//       <div className="metric-header clickable" onClick={() => setExpanded(!expanded)}>
//         <div>
//           <h3 className="metric-title">{metric.name}</h3>
//           <p className="metric-category">{categoryName}</p>
//         </div>
//         <div className="metric-value-group">
//           <div className="metric-value">
//             {metric.value}
//             {metric.unit && <span className="metric-unit"> {metric.unit}</span>}
//           </div>
//           <div className={`metric-status ${metric.status}`}>
//             {metric.status === 'success' ? '✓ Optimal' : metric.status === 'warning' ? '⚠ Borderline' : '❌ Low'}
//           </div>
//         </div>
//       </div>

//       {expanded && (
//         <div className="metric-expanded">
//           <div className="metric-bar-wrapper">
//             <div className="metric-bar-bg" />
//             {threshold && (
//               <div className="metric-threshold" style={{ left: `${threshold}%` }}>
//                 <span className="metric-threshold-label">{whoLabel}</span>
//               </div>
//             )}
//             <div className="metric-dot" style={{ left: `${position}%`, backgroundColor: color }}></div>
//           </div>

//           <p className="metric-description">{metric.description}</p>

//           {metric.educationalContent && (
//             <>
//               <h4 className="metric-subheading">Why This Matters</h4>
//               <p>{metric.educationalContent.description}</p>

//               {metric.educationalContent.improvementTips?.length > 0 && (
//                 <>
//                   <h4 className="metric-subheading">How to Improve</h4>
//                   <ul className="metric-tips">
//                     {metric.educationalContent.improvementTips.map((tip, i) => (
//                       <li key={i}>{tip}</li>
//                     ))}
//                   </ul>
//                 </>
//               )}
//             </>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// MetricCard.propTypes = {
//   metric: PropTypes.object.isRequired,
//   categoryName: PropTypes.string.isRequired,
// };

// export default MetricCard;
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import '../Reports/components/DetailedAnalysis/DetailedAnalysis.scss';

const MetricCard = ({ metric }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const formatWhoCutoff = (cutoff) => {
    if (!cutoff) return null;
    const match = cutoff.match(/([<>≥≤=]*\s*)([\d.]+)\s*([a-zA-Z/%μ]+)?/);
    if (!match) return null;
    const [, , value, unit] = match;
    return `${value}+ ${unit || ''}`.trim();
  };

  const formattedCutoff = metric.who_cutoff ? formatWhoCutoff(metric.who_cutoff) : null;

  const buildRangeLabel = () => {
    if (metric.hormone_range) return `Normal: ${metric.hormone_range.normal}`;
    if (metric.range_low && metric.range_normal && metric.range_great) {
      const u = metric.range_unit ? ` ${metric.range_unit}` : '';
      const isInverse = metric.range_low?.startsWith('>') || metric.range_low?.startsWith('≥');
      const lLow = metric.label_low || (isInverse ? 'High' : 'Low');
      const lNormal = metric.label_normal || 'Normal';
      const lGreat = metric.label_great || 'Great';
      const cleanLow = metric.range_low.replace(/^[><=≥≤]+/, '');
      const cleanGreat = metric.range_great.replace(/^[><=≥≤]+/, '');
      const geSymbol = metric.range_low?.startsWith('≥') ? '≥' : '>';
      if (isInverse) return `${lGreat} < ${cleanGreat}${u} · ${lNormal} ${metric.range_normal}${u} · ${lLow} ${geSymbol} ${cleanLow}${u}`;
      return `${lLow} < ${cleanLow}${u} · ${lNormal} ${metric.range_normal}${u} · ${lGreat} > ${cleanGreat}${u}`;
    }
    return formattedCutoff ? `Normal: ${formattedCutoff}` : null;
  };

  const whoLabel = buildRangeLabel();

  const getStatusLabel = () => {
    const parseVal = (v) => parseFloat(String(v).replace(/^[^\d.-]+/, ''));
    if (metric.hormone_range) {
      const val = parseVal(metric.value);
      const highMatch = metric.hormone_range.high?.match(/[\d.]+/);
      const lowMatch = metric.hormone_range.low?.match(/[\d.]+/);
      const highVal = highMatch ? parseFloat(highMatch[0]) : null;
      const lowVal = lowMatch ? parseFloat(lowMatch[0]) : null;
      if (highVal !== null && val > highVal) return { label: '❌ High', cls: 'error' };
      if (lowVal !== null && val < lowVal) return { label: '❌ Low', cls: 'error' };
      return { label: '✓ Normal', cls: 'success' };
    }
    if (metric.range_low && metric.range_great) {
      const val = parseVal(metric.value);
      const isInverse = metric.range_low?.startsWith('>') || metric.range_low?.startsWith('≥');
      if (isInverse) {
        const highThresh = parseFloat(metric.range_low.replace(/^[><=≥≤]+/, ''));
        const greatThresh = parseFloat(metric.range_great.replace(/^[><=≥≤]+/, ''));
        if (val >= highThresh) return { label: `❌ ${metric.label_low || 'High'}`, cls: 'error' };
        if (val < greatThresh) return { label: `✓ ${metric.label_great || 'Great'}`, cls: 'success' };
        return { label: `⚠ ${metric.label_normal || 'Normal'}`, cls: 'warning' };
      }
      const lowThresh = parseFloat(metric.range_low.replace(/^[><=≥≤]+/, ''));
      const greatThresh = parseFloat(metric.range_great.replace(/^[><=≥≤]+/, ''));
      if (val < lowThresh) return { label: `❌ ${metric.label_low || 'Low'}`, cls: 'error' };
      if (val >= greatThresh) return { label: `✓ ${metric.label_great || 'Great'}`, cls: 'success' };
      return { label: `⚠ ${metric.label_normal || 'Normal'}`, cls: 'warning' };
    }
    // fallback to backend status
    if (metric.status === 'success') return { label: '✓ Optimal', cls: 'success' };
    if (metric.status === 'warning') return { label: '⚠ Borderline', cls: 'warning' };
    if (metric.status === 'danger' || metric.status === 'error') return { label: '❌ Low', cls: 'error' };
    return { label: '❌ Low', cls: 'error' };
  };

  const statusInfo = getStatusLabel();

  return (
    <div className="metric-card" onClick={() => setIsOpen(!isOpen)}>
      <div className="metric-header clickable" onClick={() => setExpanded(!expanded)}>
        <div>
          <h3 className="metric-title">
            {metric.name}
            <svg width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16.8227 0.813477L10.0188 7.61741L6.01649 3.6151L0.813477 8.8181"
                stroke="#1235FA"
                strokeWidth="1.62698"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M12.0186 0.813477H16.8213V5.61625" stroke="#1235FA" strokeWidth="1.62698" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </h3>
          <div className="metric-value">
            Your result:{' '}
            {(() => {
              const v = metric.value ?? metric.result ?? metric.raw_value ?? metric.display_value;
              if (v === null || v === undefined) return 'N/A';
              const str = String(v);
              const prefix = str.match(/^[<>≥≤]+/)?.[0] || '';
              const num = parseFloat(str.replace(/^[<>≥≤]+/, ''));
              return isNaN(num) ? str : `${prefix}${num}`;
            })()}
            {metric.unit && <span className="metric-unit"> {metric.unit}</span>}
          </div>
          {whoLabel ? <div className="metric-normal">{whoLabel}</div> : <div className="metric-normal">No reference available</div>}
          {metric.hormone_range && (
            <div className="metric-normal" style={{ fontSize: '11px', opacity: 0.7 }}>
              Low: {metric.hormone_range.low} &nbsp;|&nbsp; High: {metric.hormone_range.high}
            </div>
          )}
          {/* <p className="metric-category">{categoryName}</p> */}
        </div>

        <div className="metric-value-group">
          <div className="metric-value-line">
            <div className="metric-status-wrapper">
              <div className={`metric-status ${statusInfo.cls}`}>{statusInfo.label}</div>
              <span className={`chevron ${isOpen ? 'open' : ''}`}>
                <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 7L7 0.999999L1 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      {expanded && !metric.hormone_range && (
        <div className="metric-expanded">
          <div className="metric-content">
            <p className="metric-description mb-3">Number of sperm per milliliter</p>
            <p className="metric-subheading">Why This Matters</p>
            <p className="metric-description mb-4">
              Sperm concentration is a key indicator of fertility. Higher concentrations increase the likelihood of successful fertilization.
            </p>
            <p className="metric-subheading">How to Improve</p>
            <ul className="metric-tips mb-0">
              <li>Maintain healthy body weight</li>
              <li>Avoid excessive heat exposure</li>
              <li>Take zinc and folic acid supplements</li>
              <li>Reduce stress levels</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

MetricCard.propTypes = {
  metric: PropTypes.object.isRequired,
  // categoryName: PropTypes.string.isRequired,
};

export default MetricCard;
